import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/wellSummaryDashboard/WellSummaryService';
import {
  WellDashboardData,
  MopTypeData,
  RodpumpClassificationItem,
  DashboardFilterArr,
  FieldProductionItem,
} from '../../types/WellSummaryDashboardTypes';

export interface AlarmData {
  mop: string;
  count: number;
}
import type { RootState } from '../../store';

// State interface
export interface WellSummaryDashboardState {
  loading: boolean;
  error: string | null;
  dashboardData: WellDashboardData | null;
  mopTypes: { [key: string]: MopTypeData } | null;
  lastUpdated: string | null;
  alarmsLoading: boolean;
  alarmsError: string | null;
  alarms: AlarmData[];
  statusLoading: Record<string, boolean>;
  statusError: Record<string, string | null>;
  statusValues: Record<string, { [mop: string]: number }>;
  fieldProduction: FieldProductionItem[];
  fieldProductionLoading: boolean;
  fieldProductionError: string | null;
  serverReturnedEmpty: boolean;
  fieldProductionLoaded: boolean;
  classificationSummary: RodpumpClassificationItem[];
  classificationSummaryLoading: boolean;
  classificationSummaryError: string | null;
  wellSummaryCalled: boolean;
  wellDataSummaryCalled: boolean;
  fetchDataSummary: boolean;
  applicationsIdMap?: Record<string, string | null>;
}

const initialState: WellSummaryDashboardState = {
  loading: false,
  error: null,
  dashboardData: null,
  mopTypes: null,
  lastUpdated: null,
  alarmsLoading: false,
  alarmsError: null,
  alarms: [],
  statusLoading: {},
  statusError: {},
  statusValues: {},
  fieldProduction: [],
  fieldProductionLoading: false,
  fieldProductionError: null,
  fieldProductionLoaded: false,
  serverReturnedEmpty: false,
  classificationSummary: [],
  classificationSummaryLoading: false,
  classificationSummaryError: null,
  wellSummaryCalled: false,
  wellDataSummaryCalled: false,
  fetchDataSummary: true,
  applicationsIdMap: {},
};

// Async thunk to fetch dashboard (unfiltered)
export const fetchWellSummaryDashboard = createAsyncThunk(
  'wellSummaryDashboard/fetch',
  async (_: void, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await apiService.getWellDashboard();

      // Only dispatch additional status calls if the main API call succeeded and API is not blocked
      // AND if we haven't already called the status APIs or they're not currently loading
      const state = getState() as RootState;
      const fetchDataSummary = state.wellSummaryDashboard.fetchDataSummary;
      const hasExistingStatusData =
        state.wellSummaryDashboard.statusValues['cycles'] ||
        state.wellSummaryDashboard.statusValues['nodata'] ||
        state.wellSummaryDashboard.statusValues['restarted'];
      const isStatusLoading =
        state.wellSummaryDashboard.statusLoading['cycles'] ||
        state.wellSummaryDashboard.statusLoading['nodata'] ||
        state.wellSummaryDashboard.statusLoading['restarted'];

      if (
        fetchDataSummary &&
        response.success &&
        response.data &&
        Array.isArray(response.data.values) &&
        response.data.values.length > 0 &&
        !hasExistingStatusData &&
        !isStatusLoading
      ) {
        console.warn('WellSummary success - dispatching status count calls');
        dispatch(fetchStatusCounts('Cycles'));
        dispatch(fetchStatusCounts('NoData'));
        dispatch(fetchStatusCounts('Restarted'));
      } else {
        console.warn('Skipping status count dispatch - conditions not met:', {
          success: response.success,
          hasData: !!(response.data && Array.isArray(response.data.values) && response.data.values.length > 0),
          noExistingStatusData: !hasExistingStatusData,
          notStatusLoading: !isStatusLoading,
        });
      }

      return response.success ? response.data : rejectWithValue(response.message || 'Failed to fetch dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      return rejectWithValue(message);
    }
  },
);

// Filtered dashboard
export const fetchFilteredWellSummary = createAsyncThunk<
  WellDashboardData,
  { mops?: string; businessUnits?: string; fields?: string; groups?: string },
  { rejectValue: string }
>('wellSummaryDashboard/fetchFiltered', async (filters, { rejectWithValue }) => {
  try {
    const response = await apiService.getWellDashboard(filters);
    if (!response.success) {
      return rejectWithValue(response.message || 'Failed to fetch filtered dashboard');
    }
    return response.data;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Unknown error');
  }
});

// Status counts
export const fetchStatusCounts = createAsyncThunk(
  'wellSummaryDashboard/fetchStatusCounts',
  async (status: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getStatusCounts(status);
      if (!response.success) {
        return rejectWithValue(response.message || `Failed to fetch ${status} counts`);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Unknown error');
    }
  },
  {
    condition: (status: string, { getState }) => {
      const state = getState() as RootState;
      const key = status.toLowerCase();
      if (state.wellSummaryDashboard.statusLoading[key]) return false;
      const hasValues = !!state.wellSummaryDashboard.statusValues[key];
      if (hasValues && Object.keys(state.wellSummaryDashboard.statusValues[key]).length > 0) return false;
      return true;
    },
  },
);

// NEW thunk: fetch status counts with filters
export const fetchStatusCountsWithFilters = createAsyncThunk(
  'wellSummaryDashboard/fetchStatusCountsWithFilters',
  async (
    payload: { status: string; filters?: { mops?: string; businessUnits?: string; fields?: string; groups?: string } },
    { rejectWithValue },
  ) => {
    try {
      const { status, filters } = payload;
      const response = await apiService.getStatusCounts(status, filters);
      if (!response.success) {
        return rejectWithValue(response.message || `Failed to fetch ${status} counts`);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Unknown error');
    }
  },
);

// Field production
export const fetchFieldProduction = createAsyncThunk<
  any[],
  { mops?: string; businessUnits?: string; fields?: string; groups?: string },
  { rejectValue: string }
>('wellSummaryDashboard/fetchFieldProduction', async (filters, { rejectWithValue }) => {
  try {
    return await apiService.getFieldProduction(filters);
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch field production');
  }
});

// Thunk for alarms data
export const fetchAlarmsData = createAsyncThunk<AlarmData[], DashboardFilterArr, { rejectValue: string }>(
  'wellSummaryDashboard/fetchAlarms',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await apiService.getAlarmSummary(filters);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to fetch alarm data');
      }
      return response.data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Unknown error');
    }
  },
);
export const fetchRodpumpclassification = createAsyncThunk<any[], DashboardFilterArr, { rejectValue: string }>(
  'summaryDashboard/getRodpumpclassification',
  async (filters, { rejectWithValue }) => {
    try {
      return await apiService.getRodpumpclassification(filters);
    } catch (err) {
      return rejectWithValue('Failed to load  groups');
    }
  },
);

export const fetchApplicationsIdMap = createAsyncThunk<Record<string, string | null>, void>(
  'wellSummaryDashboard/fetchApplicationsIdMap',
  async () => {
    const wellDataSummary = await apiService.getWellDataSummary();
    const values = wellDataSummary?.values || [];
    const map: Record<string, string | null> = {};
    values.forEach((v: any) => {
      map[v.mop] = v.applicationsId ?? null;
    });
    return map;
  },
);

const wellSummaryDashboardSlice = createSlice({
  name: 'wellSummaryDashboard',
  initialState,
  reducers: {
    setDashboardData: (state, action: PayloadAction<WellDashboardData>) => {
      state.dashboardData = action.payload;
      const { mopTypes } = apiService.transformToLegacyFormat(action.payload);
      state.mopTypes = state.mopTypes ? { ...state.mopTypes, ...mopTypes } : mopTypes;
      state.lastUpdated = new Date().toISOString();
      state.serverReturnedEmpty = false;
    },
    setFetchDataSummary: (state, action: PayloadAction<boolean>) => {
      state.fetchDataSummary = action.payload;
    },
    reset: () => initialState,
    clearStatusValues: (state) => {
      state.statusValues = {};
      state.statusLoading = {};
      state.statusError = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Normal fetch
      .addCase(fetchWellSummaryDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.wellSummaryCalled = true;
      })
      .addCase(fetchWellSummaryDashboard.fulfilled, (state, action: PayloadAction<WellDashboardData>) => {
        state.loading = false;
        state.dashboardData = action.payload;
        const { mopTypes } = apiService.transformToLegacyFormat(action.payload);
        state.mopTypes = state.mopTypes ? { ...state.mopTypes, ...mopTypes } : mopTypes;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchFilteredWellSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.wellSummaryCalled = true;
      })
      .addCase(fetchFilteredWellSummary.fulfilled, (state, action: PayloadAction<WellDashboardData>) => {
        state.loading = false;
        state.dashboardData = action.payload;
        const { mopTypes } = apiService.transformToLegacyFormat(action.payload);
        state.mopTypes = state.mopTypes ? { ...state.mopTypes, ...mopTypes } : mopTypes;
        state.lastUpdated = new Date().toISOString();
        state.serverReturnedEmpty = Array.isArray(action.payload.values) && action.payload.values.length === 0;
      })
      .addCase(fetchFilteredWellSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error?.message || 'Failed to fetch filtered well summary';
      })

      // Field production
      .addCase(fetchFieldProduction.pending, (state) => {
        state.fieldProductionLoading = true;
        state.fieldProductionError = null;
      })
      .addCase(fetchFieldProduction.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.fieldProductionLoading = false;
        state.fieldProduction = action.payload;
      })
      .addCase(fetchFieldProduction.rejected, (state, action) => {
        state.fieldProductionLoading = false;
        state.fieldProductionError =
          (action.payload as string) || action.error?.message || 'Failed to load field production';
        state.fieldProduction = [];
      })

      // Alarms
      .addCase(fetchAlarmsData.pending, (state) => {
        state.alarmsLoading = true;
        state.alarmsError = null;
      })
      .addCase(fetchAlarmsData.fulfilled, (state, action: PayloadAction<AlarmData[]>) => {
        state.alarmsLoading = false;
        state.alarms = action.payload;
      })
      .addCase(fetchAlarmsData.rejected, (state, action) => {
        state.alarmsLoading = false;
        state.alarmsError = (action.payload as string) || 'Failed to load alarms data';
      })
      .addCase(fetchRodpumpclassification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRodpumpclassification.fulfilled, (state, action) => {
        state.classificationSummary = action.payload;
        state.loading = false;
      })
      .addCase(fetchRodpumpclassification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Status counts
      .addCase(fetchStatusCounts.pending, (state, action) => {
        const status = action.meta.arg.toLowerCase();
        state.statusLoading[status] = true;
        state.statusError[status] = null;
        // Set wellDataSummaryCalled flag for statuses that use WellDataSummary API
        if (status === 'cycles' || status === 'nodata' || status === 'restarted') {
          state.wellDataSummaryCalled = true;
        }
      })
      .addCase(
        fetchStatusCounts.fulfilled,
        (state, action: PayloadAction<{ status: string; values: { mop: string; count: number }[] }>) => {
          const status = action.payload.status.toLowerCase();
          state.statusLoading[status] = false;
          const map: { [mop: string]: number } = {};
          action.payload.values.forEach((v) => {
            map[v.mop.toLowerCase().replace(/\s+/g, '')] = v.count;
          });
          state.statusValues[status] = map;
          if (!state.mopTypes) state.mopTypes = {};
          const mopTypesRef = state.mopTypes;
          action.payload.values.forEach((v) => {
            const mopKey = v.mop.toLowerCase().replace(/\s+/g, '');
            if (!mopTypesRef[mopKey]) {
              mopTypesRef[mopKey] = {
                name: mopKey,
                displayName: v.mop,
                active: 0,
                running: 0,
                shutdown: 0,
                criticalFailure: 0,
                restarted: 0,
                cycles: 0,
                offline: 0,
                noData: 0,
                commFail: 0,
              };
            }
          });
          Object.entries(state.mopTypes).forEach(([mopKey, mopData]) => {
            const newVal = map[mopKey] ?? 0;
            switch (status) {
              case 'active':
                mopData.active = newVal;
                break;
              case 'running':
                mopData.running = newVal;
                break;
              case 'shutdown':
                mopData.shutdown = newVal;
                break;
              case 'restarted':
                mopData.restarted = newVal;
                break;
              case 'commfail':
              case 'commfailure':
                mopData.commFail = newVal;
                break;
              case 'offline':
                mopData.offline = newVal;
                break;
              case 'cycles':
                mopData.cycles = newVal;
                break;
              case 'nodata':
                mopData.noData = newVal;
                break;
              default:
                break;
            }
          });
          state.lastUpdated = new Date().toISOString();
        },
      )
      .addCase(fetchStatusCounts.rejected, (state, action) => {
        const status = action.meta.arg.toLowerCase();
        state.statusLoading[status] = false;
        state.statusError[status] =
          (action.payload as string) || action.error?.message || `Failed to load ${status} counts`;
      })

      // 🔹 NEW: Status counts with filters
      .addCase(fetchStatusCountsWithFilters.pending, (state, action) => {
        const status = (action.meta.arg as any).status.toLowerCase();
        state.statusLoading[status] = true;
        state.statusError[status] = null;
        // Set wellDataSummaryCalled flag for statuses that use WellDataSummary API
        if (status === 'cycles' || status === 'nodata' || status === 'restarted') {
          state.wellDataSummaryCalled = true;
        }
      })
      .addCase(fetchStatusCountsWithFilters.fulfilled, (state, action) => {
        const status = action.payload.status.toLowerCase();
        state.statusLoading[status] = false;
        const map: { [mop: string]: number } = {};
        action.payload.values.forEach((v: any) => {
          map[v.mop.toLowerCase().replace(/\s+/g, '')] = v.count;
        });
        state.statusValues[status] = map;
        if (!state.mopTypes) state.mopTypes = {};
        const mopTypesRef = state.mopTypes;
        action.payload.values.forEach((v: any) => {
          const mopKey = v.mop.toLowerCase().replace(/\s+/g, '');
          if (!mopTypesRef[mopKey]) {
            mopTypesRef[mopKey] = {
              name: mopKey,
              displayName: v.mop,
              active: 0,
              running: 0,
              shutdown: 0,
              criticalFailure: 0,
              restarted: 0,
              cycles: 0,
              offline: 0,
              noData: 0,
              commFail: 0,
            };
          }
        });
        Object.entries(mopTypesRef).forEach(([mopKey, mopData]) => {
          const newVal = map[mopKey] ?? 0;
          switch (status) {
            case 'active':
              mopData.active = newVal;
              break;
            case 'running':
              mopData.running = newVal;
              break;
            case 'shutdown':
              mopData.shutdown = newVal;
              break;
            case 'restarted':
              mopData.restarted = newVal;
              break;
            case 'commfail':
            case 'commfailure':
              mopData.commFail = newVal;
              break;
            case 'offline':
              mopData.offline = newVal;
              break;
            case 'cycles':
              mopData.cycles = newVal;
              break;
            case 'nodata':
              mopData.noData = newVal;
              break;
            default:
              break;
          }
        });
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchStatusCountsWithFilters.rejected, (state, action) => {
        const status = (action.meta.arg as any).status.toLowerCase();
        state.statusLoading[status] = false;
        state.statusError[status] =
          (action.payload as string) || action.error?.message || `Failed to load ${status} counts`;
      })
      .addCase(fetchApplicationsIdMap.fulfilled, (state, action: PayloadAction<Record<string, string | null>>) => {
        state.applicationsIdMap = action.payload;
      });
  },
});

export const { setDashboardData, setFetchDataSummary, clearStatusValues, reset } = wellSummaryDashboardSlice.actions;
export default wellSummaryDashboardSlice.reducer;

// Selectors
export const selectWellSummaryLoading = (state: RootState) => state.wellSummaryDashboard.loading;
export const selectWellSummaryError = (state: RootState) => state.wellSummaryDashboard.error;
export const selectWellDashboardRaw = (state: RootState) => state.wellSummaryDashboard.dashboardData;
export const selectWellMopTypes = (state: RootState) => state.wellSummaryDashboard.mopTypes;
export const selectWellSummaryLastUpdated = (state: RootState) => state.wellSummaryDashboard.lastUpdated;
export const selectAlarms = (state: RootState) => state.wellSummaryDashboard.alarms;
export const selectAlarmsLoading = (state: RootState) => state.wellSummaryDashboard.alarmsLoading;
export const selectAlarmsError = (state: RootState) => state.wellSummaryDashboard.alarmsError;
export const selectStatusLoading = (state: RootState, status: string) =>
  state.wellSummaryDashboard.statusLoading[status.toLowerCase()] || false;
export const selectStatusError = (state: RootState, status: string) =>
  state.wellSummaryDashboard.statusError[status.toLowerCase()] || null;
export const selectStatusValues = (state: RootState, status: string) =>
  state.wellSummaryDashboard.statusValues[status.toLowerCase()] || {};
export const selectFieldProduction = (state: RootState) => state.wellSummaryDashboard.fieldProduction;
export const selectFieldProductionLoading = (state: RootState) => state.wellSummaryDashboard.fieldProductionLoading;
export const selectFieldProductionError = (state: RootState) => state.wellSummaryDashboard.fieldProductionError;
export const selectServerReturnedEmpty = (state: RootState) => state.wellSummaryDashboard.serverReturnedEmpty;
export const selectClassificationSummary = (state: RootState) => state.wellSummaryDashboard.classificationSummary;
export const selectWellSummaryCalled = (state: RootState) => state.wellSummaryDashboard.wellSummaryCalled;
export const selectWellDataSummaryCalled = (state: RootState) => state.wellSummaryDashboard.wellDataSummaryCalled;
export const selectFetchDataSummary = (state: RootState) => state.wellSummaryDashboard.fetchDataSummary;
export const selectApplicationsIdMap = (state: RootState) => state.wellSummaryDashboard.applicationsIdMap || {};
